import { useLogout } from "@refinedev/core";
import {
  BadgePercent,
  Banknote,
  BellRing,
  Calculator,
  CalendarRange,
  Camera,
  ChartColumn,
  ChartColumnBig,
  ChevronRight,
  ClipboardList,
  FileChartColumn,
  Coins,
  Container,
  Eye,
  Factory,
  Gavel,
  History,
  Images,
  KeyRound,
  Layers,
  Lightbulb,
  RefreshCw,
  LayoutDashboard,
  ListChecks,
  ListTodo,
  LogOut,
  Menu,
  Moon,
  PackageCheck,
  PackageOpen,
  Percent,
  QrCode,
  ReceiptText,
  ScanSearch,
  ShieldAlert,
  Ruler,
  Shirt,
  Sparkles,
  Tags,
  Swords,
  Table2,
  TrendingUp,
  Truck,
  Undo2,
  Wallet,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router";

import { Annotator } from "@/components/Annotator";
import { NavCommand } from "@/components/NavCommand";
import { has, PAYMENTS_VIEW, PERMISSIONS_EDIT, PROFIT_VIEW, type PermissionCode, useIdentity } from "@/lib/access";
import { useEscapeLayer } from "@/lib/escapeStack";
import { loginPath } from "@/lib/loginReturn";
import { TOPBAR_SLOT_ID } from "@/lib/productNav";

/**
 * Пункт меню. `to` ОБЯЗАТЕЛЕН, и это не мелочь: кнопка, за которой нет экрана,
 * — обещание, которого кабинет не исполняет, ровно как ссылка «восстановить
 * пароль» в форме входа, которой у нас нет по той же причине. Муляжи убраны
 * 06.09.26 вместе с опциональностью, чтобы новый муляж пришлось заводить
 * осознанно, а не забыв дописать адрес.
 *
 * Активность считается от адреса, а не флагом: страниц больше одной.
 *
 * `also` — префиксы адресов, на которых пункт тоже подсвечен. `/supplier/{id}`
 * с 25.09.26 — не «Товары», а место карточки поставщика; товары поставщика —
 * `/products?supplier=`.
 */
type NavItem = { label: string; icon: ReactNode; to: string; also?: string[]; need?: PermissionCode };

function isActive(item: NavItem, pathname: string): boolean {
  return item.to === pathname || (item.also ?? []).some((prefix) => pathname.startsWith(prefix));
}

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: "Дашборд",
    items: [
      // Журнал сигналов — цена, штрафы, перемеры (21.10.26, решение владельца,
      // спека control-informers §7): первым пунктом, без права, как «Штрафы».
      { label: "Контроль", icon: <BellRing size={14} />, to: "/dashboard/control" },
      {
        label: "Остатки",
        icon: <LayoutDashboard size={14} />,
        to: "/dashboard/stock",
      },
      // Товары с малым числом фото WB и путь до Краснодара (17.09.26, для
      // руководителя фотосессий).
      { label: "Фотосессия", icon: <Camera size={14} />, to: "/dashboard/photoshoot" },
    ],
  },
  // АССОРТИМЕНТ СТОИТ НАД ЗАКУПКОЙ, А НЕ ВНУТРИ НЕЁ (16.09.26, решение
  // владельца: «над закупкой сделай блок „Товары“… „Товары“ будет как бы
  // подразделом выше „Закупки“»). Пункт заведён накануне в «Закупке», после
  // «Поставщиков», по пути «у кого есть что заказать» → «что у него
  // продаётся»; владелец читает его иначе — то, ЧЕМ торгуют, стоит над
  // вопросом «что дозаказать». Группа с одним пунктом намеренно: «первый
  // пункт» сказано про место, а не про одиночество.
  {
    group: "Товары",
    items: [
      // Склейки поставщика либо всего ассортимента (15.09.26).
      { label: "Товары", icon: <Shirt size={14} />, to: "/products" },
      // Теги товаров (разметка 29.09.26 15:45): последние списки товаров.
      { label: "Теги", icon: <Tags size={14} />, to: "/tags" },
      // Объём карточек по корзинам и активные перемеры (22.10.26).
      { label: "Габариты", icon: <Ruler size={14} />, to: "/dimensions" },
    ],
  },
  // КОНТЕНТ — ЧТО МЫ МЕНЯЕМ В САМИХ КАРТОЧКАХ ПЛОЩАДКИ (17.09.26, просьба
  // владельца: «отдельный раздел „Контент“, а в „Изменениях“ — история, когда
  // карточка менялась»). Стоит рядом с «Товарами»: вопрос тот же — про вещь, а
  // не про закупку и деньги.
  {
    group: "Контент",
    items: [
      // Всё, что в карточках требует контроля (21.10.26, заказ владельца: первым
      // пунктом «Контента»). Первая проверка — баркод Ozon против размера WB.
      { label: "Контроль", icon: <ShieldAlert size={14} />, to: "/content/control" },
      {
        label: "Изменения",
        icon: <History size={14} />,
        to: "/content/changes",
        also: ["/content/changes/"],
      },
      // Кандидаты площадки по фото и разметка «конкурент / не конкурент»
      // (28.09.26): по размеченным потом следим за ценами конкурентов.
      { label: "Конкуренты", icon: <Swords size={14} />, to: "/content/rivals" },
      // Какие кадры в каких слотах WB менялись (29.09.26): контент-менеджер
      // перезаливает фото после фотосессий, лента пишется раз в сутки.
      { label: "Смены фото", icon: <Images size={14} />, to: "/content/photos" },
      // Товары в коробах Ростова и Воронежа без карточки Ozon (21.10.26):
      // FBS может отгрузить, а продать на Ozon нечем — очередь заведения.
      { label: "Очередь", icon: <ListTodo size={14} />, to: "/content/queue" },
    ],
  },
  {
    group: "Закупка",
    items: [
      // Сигналы закупки одним экраном (разметка 29.09.26: «дашборд в первом меню»).
      { label: "Дашборд", icon: <LayoutDashboard size={14} />, to: "/dashboard/purchase" },
      // Вход в карточку QR-папки по номеру (28.09.26, заказ владельца: первым пунктом).
      { label: "BQD", icon: <QrCode size={14} />, to: "/bqd", also: ["/bqd/"] },
      { label: "Планы", icon: <Table2 size={14} />, to: "/plans" },
      { label: "Заказы", icon: <ClipboardList size={14} />, to: "/orders", also: ["/orders/"] },
      // Деньги НАРУЖУ, фабрикам за товар (разметка 29.09.26 07:30: «оплаты
      // относятся к закупке»). Переехали из «Финансов»: в цепочку финотчёта
      // WB ни одно число отсюда не входит, а стоят рядом с заказом, за
      // который платят. Адрес `/finance/payments` прежний — на него ведут
      // ссылки меню поставщика.
      { label: "Оплаты", icon: <Banknote size={14} />, to: "/finance/payments", need: PAYMENTS_VIEW },
      // Лента карго-накладных от свежих к старым (28.09.26, спека 2026-09-28-cargo-list-design.md).
      { label: "Карго-накладные", icon: <Container size={14} />, to: "/cargo" },
      { label: "Сезонность", icon: <CalendarRange size={14} />, to: "/season" },
      { label: "Спрос", icon: <ChartColumnBig size={14} />, to: "/demand" },
      { label: "Статистика", icon: <TrendingUp size={14} />, to: "/stat" },
      // Основообразующие фразы категорий и их частотность год к году по Джему
      // (03.10.26, спека 2026-10-03-seo-keywords-design.md).
      { label: "SEO", icon: <ScanSearch size={14} />, to: "/seo" },
      { label: "Поставщики", icon: <Factory size={14} />, to: "/suppliers", also: ["/supplier/"] },
      // День приёмщика — тот же раздел: без префикса заголовок на прямой ссылке
      // `/priemka/day/…` называл бы «Планы», а меню не подсвечивало ничего.
      { label: "Приёмка", icon: <PackageCheck size={14} />, to: "/priemka", also: ["/priemka/"] },
    ],
  },
  // ПОСТАВКА ИДЁТ ПОСЛЕ ПЛАНА И ЗАКАЗА И ДО ДЕНЕГ (23.09.26, заказ владельца:
  // «сделаем отдельный подраздел в меню „Поставки“ — поставки, которые едут на
  // ВБ, и поставки, которые едут на Озон»). Экран Ozon — 29.09.26, заявки
  // из схемы соседа `ozon`.
  {
    group: "Поставки",
    items: [
      { label: "Wildberries", icon: <Truck size={14} />, to: "/supply/wb", also: ["/supply/wb/"] },
      { label: "Ozon", icon: <Truck size={14} />, to: "/supply/ozon", also: ["/supply/ozon/"] },
      // Какие короба везти на склад WB — глазами ДО поставки (25.09.26).
      // Подсорт двух видов (21.10.26): коробами целиком и штуками из коробов.
      { label: "Подсорт короба", icon: <Calculator size={14} />, to: "/supply/podsort-korob" },
      // Товар с последним коробом — штуками, задачами подсорта `fbs` (28.09.26).
      { label: "Подсорт штуки", icon: <PackageOpen size={14} />, to: "/supply/podsort" },
    ],
  },
  // НАШ СКЛАД НА ВИТРИНАХ (20.10.26): «Контроль» — товар держится на витрине
  // только выгрузкой FBS города, ссылки проверить глазами. «Статистика» —
  // заказы FBS площадки по складам по дням.
  {
    group: "FBS",
    items: [
      // Без `also: ["/fbs"]`: префикс подсвечивал «Контроль» на любом адресе
      // раздела, а голый `/fbs` и так уводит на `/fbs/control`.
      { label: "Контроль", icon: <Eye size={14} />, to: "/fbs/control" },
      { label: "Статистика", icon: <ChartColumn size={14} />, to: "/fbs/stat" },
    ],
  },
  // ГДЕ ЛЕЖИТ КАЖДЫЙ ВОЗВРАТ (21.10.26): по дню получения — с серией ли, в
  // коробе ли, в какой группе мест ждёт. Карточка дня `/returns/:mp/:day`
  // подсвечивает тот же пункт.
  {
    group: "Возвраты",
    items: [{ label: "Дашборд", icon: <Undo2 size={14} />, to: "/returns", also: ["/returns/"] }],
  },
  // ДЕНЬГИ WILDBERRIES СОБРАНЫ В ОДНУ ГРУППУ, и это не косметика: «Баланс» и
  // «Финотчёт» отвечают на один и тот же вопрос с разных сторон — сколько
  // площадка начислила и сколько из этого дошло. Пока баланс висел в
  // «Дашборде», а отчёт в «Закупке», сверка требовала прыжка через всё меню, а
  // закупка соседствовала с документами, к которым отношения не имеет.
  {
    group: "Финансы",
    items: [
      { label: "Баланс", icon: <Wallet size={14} />, to: "/finance/balance" },
      { label: "Финотчёт", icon: <ReceiptText size={14} />, to: "/report" },
      // Штрафы стоят сразу за «Финотчётом», потому что деньги WB берут оттуда
      // же — строкой «Общая сумма штрафов» его сводки, только разложенной по
      // категориям и вместе с Ozon. Без права: штрафы — не прибыль и не цены,
      // а вопрос «за что нас наказывают», касается он всех.
      { label: "Штрафы", icon: <Gavel size={14} />, to: "/finance/penalties" },
      // Третий вопрос той же группы: сколько из начисленного НАШЕ, и по каким
      // товарам. Стоит после «Финотчёта», потому что раскладывает ровно его
      // недельный документ — и в его же цепочку обязан сложиться.
      { label: "Прибыль", icon: <Coins size={14} />, to: "/finance/profit", need: PROFIT_VIEW },
      // Четвёртый вопрос: по какой цене и с какой скидкой у нас забирают вещь.
      // «Прибыль» отвечает, сколько денег осталось после возвратов и расходов,
      // а здесь возвратов нет вовсе — только то, что видит покупатель при
      // заказе, и разрез не по товару, а по категории.
      // Вкладка «Цена установить» живёт под тем же пунктом (`/finance/price/set`).
      { label: "Цены", icon: <Percent size={14} />, to: "/finance/price", also: ["/finance/price/"] },
    ],
  },
  // АКЦИИ WB (23.10.26, владелец): календарь акций и автоакций из API и
  // кабинета, товар × акции и предложения «что добавить» — спека wb-promo.
  {
    group: "Акции",
    items: [
      { label: "Календарь", icon: <BadgePercent size={14} />, to: "/promo" },
      { label: "По товарам", icon: <Layers size={14} />, to: "/promo/products" },
      { label: "Предложения", icon: <Lightbulb size={14} />, to: "/promo/offers", need: PROFIT_VIEW },
      { label: "Рекомендатор", icon: <Sparkles size={14} />, to: "/promo/advisor", need: PROFIT_VIEW },
      // Промо-раннер (29.10.26): последний прогон и его решения — спека promo-runner.
      { label: "Решения", icon: <ListChecks size={14} />, to: "/promo/decisions", need: PROFIT_VIEW },
    ],
  },
  // РАЗОВЫЕ РАЗБОРЫ С ЦИФРАМИ (22.10.26, владелец: «разборы supply — в supply,
  // а не в prokat»): markdown из `docs/analysis/`, в чат уходит ссылка.
  {
    group: "Анализ",
    items: [{ label: "Разборы", icon: <FileChartColumn size={14} />, to: "/analysis", also: ["/analysis/"] }],
  },
  // НАСТРОЙКИ — ПОСЛЕДНИМИ (17.09.26): это не рабочий вопрос закупки, а кто что
  // может в кабинете. Пункт видит только тот, кто управляет полномочиями.
  {
    group: "Настройки",
    items: [
      // «Синхронизация» — без права: раздел не показывает ни прибыли, ни цен,
      // а вопрос «работает ли фон» касается всех, кто смотрит на числа.
      { label: "Синхронизация", icon: <RefreshCw size={14} />, to: "/settings/sync" },
      { label: "Полномочия", icon: <KeyRound size={14} />, to: "/settings/permissions", need: PERMISSIONS_EDIT },
    ],
  },
];

/**
 * Каркас кабинета.
 *
 * Приложение занимает ровно экран, а прокручивается только рабочая область
 * таблицы. Так шапка таблицы и панель отбора остаются перед глазами, и — что
 * важнее — у виртуализации появляется scroll-контейнер известной высоты,
 * без которого она не работает.
 *
 * МЕНЮ — КОЛОНКА ТОЛЬКО С `md` (768px), ниже оно выдвижное (13.10.26, замечание
 * владельца: «с мобильного очень плохо работает интерфейс, основная часть
 * экрана не просматривается»). Причина была не в экранах, а здесь: колонка
 * меню — жёсткие 232px, и на телефоне в 390px рабочей области оставалось 158,
 * а `overflow-hidden` на каркасе отбирал последнее средство — отскроллить
 * меню влево и дотянуться до содержимого. Боковую прокрутку таблиц починить
 * было нечем, пока ширину забирало меню: свои `overflow-x` у экранов есть.
 *
 * Высота — `dvh`, а не `vh`: в мобильном браузере адресная строка не входит в
 * `100vh`, и низ экрана вместе с прокруткой уезжал под неё. На десктопе
 * `dvh` равен `vh`, разницы нет.
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { data: user, isPending } = useIdentity();
  const { mutate: logout } = useLogout();
  const { pathname } = useLocation();
  // Пункт без права не рисуется, группа без пунктов — тоже: заголовок над
  // пустотой читался бы как поломка.
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter((i) => !i.need || has(user, i.need)) })).filter(
    (g) => g.items.length > 0,
  );
  // Заголовок — из ПОЛНОГО списка, не из отфильтрованного: человек без права
  // стоит на «Прибыли» или «Полномочиях» по прямой ссылке (экран сам пишет
  // «Нет доступа»), и заголовок обязан называть экран, на котором он есть, а
  // не подставлять первый попавшийся пункт меню.
  const current = NAV.flatMap((g) => g.items).find((i) => isActive(i, pathname));
  // Выдвинутое меню — разовый просмотр, и в адрес он НЕ пишется: с ним уехали
  // бы и параметры экрана под ним. Esc — через общую стопку слоёв, иначе он
  // закрыл бы меню поверх открытой панели экрана.
  const [menuOpen, setMenuOpen] = useState(false);
  useEscapeLayer(() => setMenuOpen(false), menuOpen);
  // Смена адреса гасит меню: иначе тапнул пункт и смотришь на меню, а не на
  // экран. Пункт закрывает его и сам — по своему адресу `pathname` не меняется,
  // а навести сюда может и `NavCommand`.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);
  const [theme, setTheme] = useState<"dark" | "light">(
    () =>
      (document.documentElement.dataset["theme"] as "dark" | "light") ?? "dark",
  );

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.dataset["theme"] = next;
    setTheme(next);
  };

  return (
    <div className="grid h-dvh grid-cols-[minmax(0,1fr)] overflow-hidden md:grid-cols-[var(--spacing-sidebar)_minmax(0,1fr)]">
      {/* Затемнение под выдвинутым меню: закрывает кликом, как у любой
          выезжающей панели кабинета. С `md` меню — колонка, затемнять нечего. */}
      {menuOpen ? (
        <div
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
        />
      ) : null}
      {/* Ниже `md` меню вынуто из потока (`fixed`) и съезжает за левый край, а
          не сжимает рабочую область: выдвинутое, оно ЛЕЖИТ поверх экрана, и
          содержимое под ним остаётся во всю ширину. */}
      <aside
        className={
          "fixed inset-y-0 left-0 z-50 flex w-[var(--spacing-sidebar)] flex-col border-r border-border bg-bg-subtle transition-transform md:static md:z-auto md:h-dvh md:w-auto md:translate-x-0 " +
          (menuOpen ? "translate-x-0" : "-translate-x-full")
        }
      >
        <div className="flex h-[var(--spacing-topbar)] flex-none items-center gap-2 border-b border-border px-3">
          <div
            title="supply"
            className="grid size-[22px] flex-none place-items-center rounded-[5px] bg-fg text-[12px] font-semibold text-bg"
          >
            S
          </div>
          {/* Вместо надписи «supply» — быстрая навигация командами CRM
              (разметка 29.09.26 07:31). */}
          <NavCommand menu={nav} activeTo={current?.to} onDone={() => setMenuOpen(false)} />
          {/* Контур ЧИСЕЛ, а не сборки: кабинет читает CRM того контура, на
              который смотрит юнит, и подпись обязана называть его честно —
              иначе dev-данные выглядят боевыми. */}
          <span
            title={
              user?.crm
                ? `кабинет читает CRM: ${user.crm}`
                : "контур CRM неизвестен"
            }
            className={`flex-none rounded border px-1.5 font-mono text-[10px] ${
              user?.crm_is_prod === false
                ? "border-warn text-warn"
                : "border-border text-fg-faint"
            }`}
          >
            {user?.crm_is_prod === false ? "dev" : "prod"}
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto p-2">
          {nav.map((group) => (
            <div key={group.group} className="mb-4">
              <div className="px-2 pb-1.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-fg-faint">
                {group.group}
              </div>
              {group.items.map((item) => {
                const active = isActive(item, pathname);
                const cls =
                  "flex h-8 w-full items-center gap-2.5 rounded-[var(--radius-ui)] px-2 text-[13.5px] transition-colors " +
                  (active
                    ? "bg-bg-hover font-medium text-fg"
                    : "text-fg-muted hover:bg-bg-hover hover:text-fg");
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    aria-current={active ? "page" : undefined}
                    className={cls}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Кнопки «Быстрый поиск» здесь больше нет: она ничего не делала, а
            неработающая кнопка в кабинете — обещание, которого он не
            исполняет. Вернётся вместе с поиском, а не раньше. */}
        <div className="grid flex-none gap-2 border-t border-border p-2">
          <div className="flex items-center gap-2 rounded-[var(--radius-ui)] px-1.5 py-1">
            <div className="size-[22px] flex-none rounded-full bg-accent" />
            <div className="min-w-0">
              <div className="truncate text-[12.5px]">{user?.name ?? "…"}</div>
              <div className="text-[11px] text-fg-faint">закупка</div>
            </div>
            <button
              type="button"
              onClick={() => logout()}
              title="Выйти"
              className="ml-auto rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-fg"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex h-[var(--spacing-topbar)] items-center gap-3 border-b border-border bg-bg/85 px-3 backdrop-blur md:px-5">
          {/* Единственный вход в меню на телефоне, поэтому он виден ВСЕГДА и
              размером под палец, а не иконкой по наведению. */}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Меню"
            aria-expanded={menuOpen}
            className="-ml-1 grid size-10 flex-none place-items-center rounded-[var(--radius-ui)] text-fg-muted hover:bg-bg-hover hover:text-fg md:hidden"
          >
            <Menu size={18} />
          </button>
          <div className="flex min-w-0 items-center gap-2 text-[13.5px] text-fg-muted">
            {/* Путь до экрана на телефоне съедал полшапки ни за что: кабинет
                один, и это видно. Остаётся название экрана. */}
            <span className="hidden items-center gap-2 md:flex">
              <span>uni-renter</span>
              <ChevronRight size={13} className="text-border-strong" />
              <span>supply</span>
              <ChevronRight size={13} className="text-border-strong" />
            </span>
            <span className="truncate font-medium text-fg">
              {current?.label ?? "Планы"}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* СЛОТ ЭКРАНА. Каркас про листание не знает ничего: карточка
                товара вкладывает сюда свой блок порталом, и вместе с уходом со
                страницы он исчезает сам — погасить его вручную нельзя забыть.
                Пустой узел — тоже flex-item, и `gap` родителя посчитал бы для
                него зазор на КАЖДОМ экране кабинета; `empty:hidden` выводит
                пустой слот из потока целиком. */}
            <div id={TOPBAR_SLOT_ID} className="flex items-center gap-2 empty:hidden" />
            <button
              type="button"
              onClick={toggleTheme}
              title="Тема"
              className="grid h-8 w-8 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover"
            >
              <Moon size={14} />
            </button>
          </div>
        </header>

        {/* ЭКРАН — ПОСЛЕ ЛИЧНОСТИ: права едут в ней, и экран, нарисованный до
            неё, показал бы владельцу «без прибыли» и отправил бы запрос не с
            теми настройками. `/api/me` — миллисекунды. */}
        {/* Личность, раз приехавшая, остаётся и при сорвавшемся повторном
            запросе (`getIdentity` бросает, а не отдаёт пустоту). `null` —
            это ответ 401: сессия кончилась, место человеку на форме входа. */}
        {user ? (
          children
        ) : user === null ? (
          <Navigate to={loginPath()} replace />
        ) : (
          <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 text-[12.5px] text-fg-muted">
            {isPending ? "…" : "Не удалось узнать, кто вошёл, — обновите страницу."}
          </main>
        )}
      </div>

      {/* Разметка живёт в каркасе, а не на экране: комментировать можно любой
          экран кабинета, а не только «Спрос». */}
      <Annotator />
    </div>
  );
}
